/**
 * AcceptanceGate.js
 * Non-dismissible first-login gate requiring affirmative acceptance of the
 * Terms of Service and Privacy Policy. Also the re-acceptance gate after a
 * version bump: a user holding an acceptance of an earlier version is told
 * which document changed instead of seeing the first-login copy.
 *
 * Rendered from AuthProvider, above the route table, so it is reached by
 * construction from BOTH paths that produce an authenticated state —
 * login() and setUserFromMagicLink(). Newly approved users arrive by magic
 * link, so gating only the login() path would miss exactly the people the
 * gate exists for. No route wrapper, no per-route edits.
 *
 * Posture follows PhilConsentModal: no close button, no overlay-click
 * dismissal, Escape suppressed, focus trapped. The only ways out are the two
 * buttons — accept, or decline and log out.
 *
 * Acceptance is recorded server-side, so it survives the forced logout that
 * MyAccount.js performs after a password change and does not reappear on the
 * next login.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import useFocusTrap from '../../hooks/useFocusTrap';
import { authService } from '../../services/api';
import { DOCUMENT_VERSIONS, formatDocumentDate } from '../../constants/documentVersions';
import './AcceptanceGate.css';

const DOCUMENT_NAMES = {
  tos: 'Terms of Service',
  privacy: 'Privacy Policy'
};

/**
 * Has this user accepted the CURRENT version of every gated document?
 *
 * Exported for reuse and testability. A user with an acceptance of an older
 * version has not satisfied the gate — bumping a version in
 * constants/documentVersions.js re-gates everyone by design.
 *
 * @param {Object|null} user - user object from auth context
 * @returns {boolean} true when every document has a current-version acceptance
 */
export const hasAcceptedCurrentDocuments = (user) => {
  if (!user) return false;
  const acceptances = Array.isArray(user.documentAcceptances)
    ? user.documentAcceptances
    : [];

  return Object.entries(DOCUMENT_VERSIONS).every(([documentKey, version]) =>
    acceptances.some(
      (entry) => entry?.document === documentKey && entry?.version === version
    )
  );
};

/**
 * Which gated documents has this user accepted before, but not at the current
 * version? Non-empty means they are being re-gated after a version bump and
 * should be told what changed; empty means a first acceptance.
 *
 * @param {Object|null} user - user object from auth context
 * @returns {string[]} document keys (e.g. ['tos']) in DOCUMENT_VERSIONS order
 */
export const getUpdatedDocuments = (user) => {
  const acceptances = Array.isArray(user?.documentAcceptances)
    ? user.documentAcceptances
    : [];

  return Object.entries(DOCUMENT_VERSIONS)
    .filter(([documentKey, version]) => {
      const forDocument = acceptances.filter((entry) => entry?.document === documentKey);
      return forDocument.length > 0 && !forDocument.some((entry) => entry?.version === version);
    })
    .map(([documentKey]) => documentKey);
};

/**
 * The only paths where the gate does not render.
 *
 * The gate's own checkbox links to /terms and /privacy in a new tab. Because the
 * gate renders from AuthProvider — which wraps every route — it would otherwise
 * appear over the documents in that new tab, covering the very text the user is
 * being asked to agree to.
 *
 * These two are the complete list. Do NOT extend this to other routes: every
 * addition is a page an unaccepted user can reach, which is the thing the gate
 * exists to prevent. Reading the documents is the sole justified exception.
 */
const GATE_EXEMPT_PATHS = ['/terms', '/privacy'];

/**
 * Exact path match, tolerant of one trailing slash (React Router v6 matches
 * "/terms/" to path="/terms"). Deliberately not a prefix match — "/terms-of-sale"
 * or "/privacy/export" must still be gated.
 */
const isExemptPath = (pathname) => {
  if (!pathname) return false;
  const normalized =
    pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  return GATE_EXEMPT_PATHS.includes(normalized);
};

const AcceptanceGate = ({ isOpen, user, onAccepted, onDecline }) => {
  const [checked, setChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);
  const [error, setError] = useState(null);
  const modalRef = useRef(null);
  const { pathname } = useLocation();

  // Suppressed on the legal-document routes so the user can actually read them.
  // Everything downstream — focus trap, Escape suppression, render — keys off
  // this rather than isOpen, so on /terms the gate neither traps focus nor eats
  // Escape. Acceptance state is untouched: the gate returns the moment the user
  // navigates anywhere else.
  const shouldRender = isOpen && !isExemptPath(pathname);

  useFocusTrap(modalRef, shouldRender);

  // Suppress Escape outright. PhilConsentModal achieves non-dismissibility by
  // omitting a handler entirely; we suppress explicitly so that a future
  // ancestor-level Escape handler cannot dismiss this gate out from under us.
  useEffect(() => {
    if (!shouldRender) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [shouldRender]);

  const handleAccept = useCallback(async () => {
    if (!checked || isSubmitting) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await authService.acceptDocuments();
      // The server returns the updated user; hand it straight to auth context
      // so the gate's own open condition re-evaluates to false.
      onAccepted(response.user);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          'Could not record your acceptance. Please try again.'
      );
      setIsSubmitting(false);
    }
  }, [checked, isSubmitting, onAccepted]);

  // Declining now waits on a server round trip (logout() must receive the
  // cookie-clearing response before it navigates), so the button has to be
  // held closed for the duration. A second click would re-enter logout()'s
  // isLoggingOut guard, which returns immediately without contacting the
  // server — and the caller would then navigate with the cookie still alive,
  // reproducing the exact bug this fixes.
  //
  // Deliberately never re-enabled: onDecline always navigates (logout() is
  // timeout-bounded and swallows its own errors), so the component unmounts
  // rather than sitting here disabled.
  const handleDecline = useCallback(async () => {
    if (isDeclining || isSubmitting) return;
    setIsDeclining(true);
    setError(null);
    await onDecline();
  }, [isDeclining, isSubmitting, onDecline]);

  if (!shouldRender) return null;

  // Re-acceptance after a version bump names what changed; a first acceptance
  // keeps the original copy.
  const updatedDocuments = getUpdatedDocuments(user);
  const isUpdate = updatedDocuments.length > 0;
  const updatedNames = updatedDocuments.map((key) => DOCUMENT_NAMES[key] || key);
  const updatedNamesText = updatedNames.join(' and ');

  return (
    // No onClick on the overlay — clicking outside does nothing, matching
    // PhilConsentModal. Do not add one.
    <div className="acceptance-gate-overlay">
      <div
        className="acceptance-gate-modal"
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="acceptance-gate-heading"
      >
        <h2 id="acceptance-gate-heading" className="acceptance-gate-heading">
          {isUpdate
            // "Terms of Service" takes "have"; the one other single-document
            // case, "Privacy Policy", takes "has".
            ? `Our ${updatedNamesText} ${updatedNames.length > 1 || updatedDocuments[0] === 'tos' ? 'have' : 'has'} been updated`
            : 'Before you continue'}
        </h2>

        {isUpdate && (
          <p className="acceptance-gate-body">
            {updatedDocuments
              .map((key) => `The ${DOCUMENT_NAMES[key] || key} was updated on ${formatDocumentDate(DOCUMENT_VERSIONS[key])}`)
              .join('; ')}
            {' '}since you last accepted. Please read the updated{' '}
            {updatedNames.length > 1 ? 'documents' : 'document'} and agree to
            continue using Constellation KG.
          </p>
        )}

        <label className="acceptance-gate-checkbox-row">
          <input
            type="checkbox"
            className="acceptance-gate-checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            disabled={isSubmitting}
          />
          <span className="acceptance-gate-label-text">
            I have read and agree to Constellation KG's{' '}
            {/* target="_blank" is deliberate: opening in this tab would unmount
                the gate mid-decision and drop the checkbox state. */}
            <a
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="acceptance-gate-link"
            >
              Terms of Service
            </a>{' '}
            and{' '}
            <a
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="acceptance-gate-link"
            >
              Privacy Policy
            </a>
          </span>
        </label>

        {error && (
          <p className="acceptance-gate-error" role="alert">
            {error}
          </p>
        )}

        <div className="acceptance-gate-actions">
          <button
            type="button"
            className="acceptance-gate-btn acceptance-gate-continue"
            onClick={handleAccept}
            disabled={!checked || isSubmitting || isDeclining}
          >
            {isSubmitting ? 'Saving...' : 'Continue'}
          </button>
          <button
            type="button"
            className="acceptance-gate-btn acceptance-gate-decline"
            onClick={handleDecline}
            disabled={isSubmitting || isDeclining}
          >
            {isDeclining ? 'Logging out...' : 'Decline and log out'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AcceptanceGate;
